import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Navbar.css';
import logoNavigateur from '../assets/logoNavigateur.png';
import { Snowflake, AirVent, Wind, Wrench, LayoutGrid, ChevronDown } from 'lucide-react';

/* ── Liens principaux ── */
const NAV_LINKS = [
  { label: 'Accueil',      anchor: 'accueil' },
  { label: 'À propos',     anchor: 'apropos' },
  { label: 'Services',     dropdown: true },
  { label: 'Réalisations', anchor: 'realisations' },
  { label: 'Avis',         anchor: 'avis' },
  { label: 'Contact',      anchor: 'contact' },
];

/* ── Sous-liens Services ── */
const SERVICE_LINKS = [
  {
    label: 'Tous nos services',
    path: '/solution',
    icon: <LayoutGrid size={18} strokeWidth={1.6} />,
    desc: 'Vue d\'ensemble de nos prestations',
    featured: true,
  },
  {
    label: 'Climatisation',
    path: '/climatisation',
    icon: <Snowflake size={18} strokeWidth={1.6} />,
    desc: 'Split, multi-split, gainable',
  },
  {
    label: 'Pompe à chaleur',
    path: '/pompe-a-chaleur',
    icon: <AirVent size={18} strokeWidth={1.6} />,
    desc: 'PAC air-air et air-eau',
  },
  {
    label: 'Ventilation',
    path: '/ventilation',
    icon: <Wind size={18} strokeWidth={1.6} />,
    desc: 'VMC simple et double flux',
  },
  {
    label: 'Entretien & Dépannage',
    path: '/entretien-climatisation',
    icon: <Wrench size={18} strokeWidth={1.6} />,
    desc: 'Maintenance toutes marques',
  },
];

/* ── Scroll utilitaire ── */
function getAbsoluteTop(el) {
  let top = 0, node = el;
  while (node) { top += node.offsetTop || 0; node = node.offsetParent; }
  return top;
}

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const navH = document.querySelector('.navbar')?.offsetHeight || 70;
  window.scrollTo({ top: Math.max(0, getAbsoluteTop(el) - navH), behavior: 'smooth' });
}

export default function Navbar() {
  const [scrolled,     setScrolled]     = useState(false);
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [dropOpen,     setDropOpen]     = useState(false);
  const [mobileServOpen, setMobileServOpen] = useState(false);

  const timerRef   = useRef(null);
  const dropRef    = useRef(null);
  const navigate   = useNavigate();
  const location   = useLocation();
  const isHome     = location.pathname === '/' || location.pathname === '';
  const isHomeRef  = useRef(isHome);

  useEffect(() => { isHomeRef.current = isHome; }, [isHome]);

  /* Scroll detection */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Ferme tout au changement de route */
  useEffect(() => {
    setMenuOpen(false);
    setDropOpen(false);
    setMobileServOpen(false);
  }, [location.pathname]);

  /* Body scroll lock */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  /* Scroll ancre inter-page */
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const t = setTimeout(() => scrollToSection(id), 600);
    return () => clearTimeout(t);
  }, [location]);

  /* Clic hors dropdown → ferme */
  useEffect(() => {
    const handleClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  /* Naviguer vers une page service */
  const goToService = useCallback((path) => {
    setDropOpen(false);
    setMenuOpen(false);
    setMobileServOpen(false);
    document.body.style.overflow = '';
    timerRef.current = setTimeout(() => navigate(path), 60);
  }, [navigate]);

  /* Clic nav général */
  const handleNavClick = useCallback((link) => {
    if (link.dropdown) {
      setDropOpen(v => !v);
      return;
    }
    setMenuOpen(false);
    setDropOpen(false);
    document.body.style.overflow = '';

    if (link.path) {
      timerRef.current = setTimeout(() => navigate(link.path), 60);
      return;
    }
    if (link.anchor) {
      if (isHomeRef.current) {
        timerRef.current = setTimeout(() => scrollToSection(link.anchor), 340);
      } else {
        timerRef.current = setTimeout(() => navigate(`/#${link.anchor}`), 60);
      }
    }
  }, [navigate]);

  return (
    <>
      <header className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
        <div className="navbar__inner container">

          {/* Logo */}
          <button className="navbar__logo" onClick={() => handleNavClick({ anchor: 'accueil' })} type="button" aria-label="Accueil">
            <img src={logoNavigateur} alt="Logo Soclimate" className="navbar__logo-img" />
            <span className="navbar__brand">
              <span className="navbar__brand-soc">SOC</span>
              <span className="navbar__brand-limate">LIMATE</span>
            </span>
          </button>

          {/* Nav desktop */}
          <nav className="navbar__nav" role="navigation" aria-label="Navigation principale">
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                /* ── DROPDOWN SERVICES ── */
                <div key="services" className="navbar__drop-wrap" ref={dropRef}>
                  <button
                    className={`navbar__link navbar__link--drop${dropOpen ? ' navbar__link--drop-open' : ''}`}
                    onClick={() => setDropOpen(v => !v)}
                    onMouseEnter={() => setDropOpen(true)}
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={dropOpen}
                  >
                    Services
                    <ChevronDown size={14} strokeWidth={2.5} className="navbar__drop-chevron" />
                  </button>

                  <div
                    className={`navbar__dropdown${dropOpen ? ' navbar__dropdown--open' : ''}`}
                    onMouseLeave={() => setDropOpen(false)}
                  >
                    <div className="navbar__dropdown-inner">
                      {SERVICE_LINKS.map((s) => (
                        <button
                          key={s.path}
                          className={`navbar__drop-item${s.featured ? ' navbar__drop-item--featured' : ''}`}
                          onClick={() => goToService(s.path)}
                          type="button"
                        >
                          <span className="navbar__drop-icon">{s.icon}</span>
                          <span className="navbar__drop-text">
                            <span className="navbar__drop-label">{s.label}</span>
                            <span className="navbar__drop-desc">{s.desc}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  key={link.label}
                  className="navbar__link"
                  onClick={() => handleNavClick(link)}
                  type="button"
                >
                  {link.label}
                </button>
              )
            )}
          </nav>

          {/* CTA */}
          <button className="navbar__cta" onClick={() => handleNavClick({ anchor: 'contact' })} type="button">
            Demander un devis
          </button>

          {/* Burger */}
          <button
            className={`navbar__burger${menuOpen ? ' navbar__burger--open' : ''}`}
            onClick={() => setMenuOpen(v => !v)}
            aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={menuOpen}
            type="button"
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* ── OVERLAY MOBILE ── */}
      <div className={`mobile-overlay${menuOpen ? ' mobile-overlay--open' : ''}`} aria-hidden={!menuOpen}>
        <div className="mobile-overlay__top">
          <button className="mobile-overlay__logo" onClick={() => handleNavClick({ anchor: 'accueil' })} type="button" aria-label="Accueil">
            <img src={logoNavigateur} alt="Logo Soclimate" className="mobile-overlay__logo-img" />
            <span className="mobile-overlay__brand">
              <span className="mobile-overlay__brand-soc">SOC</span>
              <span className="mobile-overlay__brand-limate">LIMATE</span>
            </span>
          </button>
          <button className="mobile-overlay__close" onClick={() => setMenuOpen(false)} type="button" aria-label="Fermer">
            <span /><span />
          </button>
        </div>

        <nav className="mobile-overlay__nav" aria-label="Navigation mobile">
          {NAV_LINKS.map((link, i) =>
            link.dropdown ? (
              /* ── SERVICES accordion mobile ── */
              <div key="services-mob" className="mob-services" style={{ '--i': i }}>
                <button
                  className={`mobile-overlay__link mob-services__toggle${mobileServOpen ? ' mob-services__toggle--open' : ''}`}
                  onClick={() => setMobileServOpen(v => !v)}
                  type="button"
                >
                  Services
                  <ChevronDown size={18} strokeWidth={2} className="mob-services__chev" />
                </button>

                <div className={`mob-services__sub${mobileServOpen ? ' mob-services__sub--open' : ''}`}>
                  {SERVICE_LINKS.map((s) => (
                    <button
                      key={s.path}
                      className="mob-services__item"
                      onClick={() => goToService(s.path)}
                      type="button"
                    >
                      <span className="mob-services__item-icon">{s.icon}</span>
                      <span className="mob-services__item-label">{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                key={link.label}
                className="mobile-overlay__link"
                onClick={() => handleNavClick(link)}
                type="button"
                style={{ '--i': i }}
              >
                {link.label}
              </button>
            )
          )}

          <button className="mobile-overlay__cta" onClick={() => handleNavClick({ anchor: 'contact' })} type="button">
            Demander un devis
          </button>
        </nav>
      </div>
    </>
  );
}